import { useNavigation, Outlet } from 'react-router-dom';
import {
  createBrowserRouter,
  RouterProvider,
  redirect,
} from 'react-router-dom';
import type { PortalNavItem } from '@/types';
import { getPortalNavItems } from '@/services';
import { LanguageContext, useLanguageState } from '@/hooks';
import { HomeLayout } from '@/components/layout';
import { HomePage } from '@/components/HomePage';
import { PharmacyLocatorPage } from '@/components/PharmacyLocatorPage';
import { ErrorPage } from '@/components/ErrorPage';
import { PageNotFound } from '@/components/PageNotFound';
import { Spinner } from '@/components/Spinner';

const loadingFallback = <Spinner message="Loading..." visible />;

/**
 * Root loader — fetches the PortalNavItem payload once and makes it
 * available to both HomeLayout and HomePage via useRouteLoaderData('root').
 * Mirrors the Angular DataResolver pattern.
 */
async function rootLoader(): Promise<PortalNavItem | Response> {
  try {
    const result = await getPortalNavItems();
    if (!result.data) {
      return redirect('/error');
    }
    return result.data;
  } catch (err) {
    return redirect('/error');
  }
}

function RootLayout() {
  const navigation = useNavigation();
  const isLoading = navigation.state === 'loading' || navigation.state === 'submitting';

  return (
    <>
      {isLoading && loadingFallback}
      <Outlet />
    </>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    loader: () => redirect('/home'),
  },
  {
    id: 'root',
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    hydrateFallbackElement: loadingFallback,
    loader: rootLoader,
    children: [
      {
        path: '/home',
        element: <HomeLayout />,
        children: [
          {
            index: true,
            element: <HomePage />,
          },
        ],
      },
      {
        path: '/pharmacylocator',
        element: <PharmacyLocatorPage />,
      },
    ],
  },
  {
    path: '/error',
    element: <ErrorPage />,
  },
  {
    path: '*',
    element: <PageNotFound />,
  },
]);

export function App() {
  const langState = useLanguageState();
  return (
    <LanguageContext.Provider value={langState}>
      <RouterProvider router={router} />
    </LanguageContext.Provider>
  );
}
